import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import {language_en} from "../translations/en";
import {language_el} from "../translations/el";

const translations = {
    en: language_en,
    el: language_el,
};

const STORAGE_KEY = 'language';

const getInitialLocale = (defaultLocale) => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved && saved in translations) return saved;
    } catch {}
    return defaultLocale;
}

const TranslationContext = createContext(null);

export const TranslationProvider = ({ children, defaultLocale = 'el' }) => {
    const [locale, setLocaleState] = useState(() => getInitialLocale(defaultLocale));

    // Changes the locale and saves it to localStorage
    const setLocale = useCallback((newLocale) => {
        setLocaleState(newLocale);
        try {
            localStorage.setItem(STORAGE_KEY, newLocale);
        } catch {}
    }, []);

    // Changes the locale for this session only, localStorage is untouched
    const setTemporaryLocale = useCallback((newLocale) => {
        setLocaleState(newLocale);
    }, []);

    const value = useMemo(() => {
        const t = (path, params) => {
            const keys = path.split('.');
            let current = translations[locale];

            for (const key of keys) {
                if (current && typeof current === 'object' && key in current) {
                    current = current[key];
                } else {
                    return path; // Fallback to path string if translation is missing
                }
            }
            if (typeof current !== 'string') return path;
            if (!params) return current;

            return current.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) =>
                name in params ? String(params[name]) : match
            );
        }
        return { locale, setLocale, setTemporaryLocale, t };
    }, [locale, setLocale, setTemporaryLocale]);

    return (
        <TranslationContext.Provider value={value}>
            {children}
        </TranslationContext.Provider>
    );
}

export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (!context) throw new Error('useTranslation must be used within a TranslationProvider');

    return context;
}